import React, { useContext, useEffect, useState } from 'react';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { AuthContext } from './../AuthContext';
import api from './../api.js';
import { isStaffUser } from './../role.js';
import { IndianCurrencyFormatter } from './../Function';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Staff.css';

function initials(name) {
  return String(name || 'S')
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function Index() {
  const { user, fulluser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, [location]);

  useEffect(() => {
    if (isStaffUser(fulluser)) {
      navigate('/home');
      return;
    }
    if (user) {
      loadStaff();
    }
  }, [user, fulluser]);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const response = await api.get('/vendor/staff');
      if (response.data.status === 'success') {
        setStaff(response.data.staff || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setForm({ name: '', phone: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setForm({ name: '', phone: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name, phone: item.phone });
    setShowForm(true);
  };

  const saveStaff = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.phone)) {
      Swal.fire('Warning', 'Enter staff name and a 10-digit mobile number', 'warning');
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `/vendor/staff/${editingId}` : '/vendor/staff';
      const response = await api.post(url, form);
      if (response.data.status === 'success') {
        Swal.fire({
          icon: 'success',
          title: editingId ? 'Staff updated' : 'Staff created',
          text: response.data.message,
          timer: 1400,
          showConfirmButton: false,
        });
        closeForm();
        loadStaff();
      } else {
        Swal.fire('Error', response.data.message || 'Failed to save staff', 'error');
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to save staff', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (item) => {
    const makingInactive = Number(item.status) === 0;
    const confirm = await Swal.fire({
      title: makingInactive ? 'Make inactive?' : 'Make active?',
      text: makingInactive
        ? `${item.name} will not be able to login.`
        : `${item.name} will be able to login again.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: makingInactive ? 'Make inactive' : 'Make active',
      confirmButtonColor: makingInactive ? '#d33' : '#6236FF',
    });
    if (!confirm.isConfirmed) return;
    try {
      const response = await api.post(`/vendor/staff/${item.id}/status`);
      if (response.data.status === 'success') {
        loadStaff();
      } else {
        Swal.fire('Error', response.data.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const activeCount = staff.filter((item) => Number(item.status) === 0).length;
  const inactiveCount = staff.length - activeCount;

  return (
    <div id="appCapsule" className="staff-page">
      <Footer />
      <Sidebar />

      <div className="section mt-2">
        <div className="section-heading">
          <h2 className="title">Staff</h2>
        </div>
        <div className="row staff-summary">
          <div className="col-4">
            <div className="stat-box">
              <div className="title">Total</div>
              <div className="value">{staff.length}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="stat-box">
              <div className="title">Active</div>
              <div className="value text-success">{activeCount}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="stat-box">
              <div className="title">Inactive</div>
              <div className="value text-danger">{inactiveCount}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="section mt-3">
        <div className="section-heading">
          <h2 className="title">Team</h2>
        </div>

        {loading ? (
          <div className="text-center p-4">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : staff.length === 0 ? (
          <div className="card">
            <div className="card-body staff-empty">
              <ion-icon name="people-outline" />
              <h4 className="mt-2 mb-1">No staff yet</h4>
              <p className="text-muted mb-3">Add a staff member with name and mobile number to let them make bills.</p>
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                Add staff
              </button>
            </div>
          </div>
        ) : (
          staff.map((item) => {
            const active = Number(item.status) === 0;
            return (
              <div className="staff-card" key={item.id}>
                <div className="staff-card-top">
                  <div className={`staff-avatar ${active ? '' : 'inactive'}`}>{initials(item.name)}</div>
                  <div className="staff-meta">
                    <h4>{item.name}</h4>
                    <div className="phone">{item.phone}</div>
                  </div>
                  <div className="staff-switch">
                    <span className={`badge ${active ? 'badge-success' : 'badge-danger'} d-block mb-1`}>
                      {active ? 'Active' : 'Inactive'}
                    </span>
                    <div className="form-check form-switch mb-0 d-flex justify-content-end">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleStatus(item)}
                      />
                    </div>
                  </div>
                </div>
                <div className="staff-stats">
                  <div>
                    <span className="lbl">Sales</span>
                    <span className="val"><IndianCurrencyFormatter amount={item.sales || 0} /></span>
                  </div>
                  <div>
                    <span className="lbl">Cash</span>
                    <span className="val"><IndianCurrencyFormatter amount={item.cash || 0} /></span>
                  </div>
                  <div>
                    <span className="lbl">Online</span>
                    <span className="val"><IndianCurrencyFormatter amount={item.online || 0} /></span>
                  </div>
                </div>
                <div className="staff-actions">
                  <button type="button" className="btn btn-outline-primary" onClick={() => openEdit(item)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/StaffReport?id=${item.id}`)}
                  >
                    Report
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {staff.length > 0 && (
        <button type="button" className="staff-fab" onClick={openCreate} aria-label="Add staff">
          +
        </button>
      )}

      {showForm && (
        <div
          className="modal fade action-sheet show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-backdrop fade show"
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1040 }}
            onClick={closeForm}
          />
          <div className="modal-dialog" role="document" style={{ zIndex: 1050 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit staff' : 'Add staff'}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeForm} />
              </div>
              <div className="modal-body">
                <form onSubmit={saveStaff}>
                  <div className="form-group basic">
                    <label className="label">Staff name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Enter name"
                    />
                  </div>
                  <div className="form-group basic">
                    <label className="label">Mobile number</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      className="form-control"
                      value={form.phone}
                      onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      placeholder="10-digit mobile"
                      maxLength="10"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                    {saving ? 'Saving...' : (editingId ? 'Update staff' : 'Create staff')}
                  </button>
                  <button type="button" className="btn btn-text btn-block mt-1" onClick={closeForm}>
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Index;
